import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import AuthHeader from "./AuthHeader";
import { getAnonId, trackEvent, trackLandingViewed, identifyUser, EventNames } from "./analytics";
import { CognitoUserPool, AuthenticationDetails, CognitoUser } from "amazon-cognito-identity-js";

const isDevHost = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname.includes("dev."));
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (isDevHost ? "https://jwdipamlyg.execute-api.us-east-1.amazonaws.com/dev" : "https://h9n9yztj57.execute-api.us-east-1.amazonaws.com/prod");
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "0x4AAAAAACH4JpItv_tU0dgj";
const COGNITO_POOL_ID = process.env.REACT_APP_COG_POOL_ID || "us-east-1_uqhqKrNLw";
const DEFAULT_COG_CLIENT_ID = isDevHost ? "t5hhev9j2906n78lcvkf39jlt" : "o2lrvtqmcfv8tl0vpam42o2r7";
const COGNITO_CLIENT_ID = process.env.REACT_APP_COG_CLIENT_ID || DEFAULT_COG_CLIENT_ID;
const DEVICE_TOKEN_KEY = "device_token";
const FREE_CREDIT_LIMIT = Number(process.env.REACT_APP_FREE_CREDITS || 1);
const FREE_CREDITS_KEY = "free_credits_used_v1";

// Guard Cognito auth when AuthenticationResult is missing to avoid NewDeviceMetadata errors
const _origAuthInternal = CognitoUser.prototype.authenticateUserInternal;
CognitoUser.prototype.authenticateUserInternal = function (dataAuthenticate, authenticationHelper, callback) {
  if (!dataAuthenticate || !dataAuthenticate.AuthenticationResult) {
    const err = new Error("Authentication failed");
    if (callback && typeof callback.onFailure === "function") {
      callback.onFailure(err);
    }
    return;
  }
  return _origAuthInternal.call(this, dataAuthenticate, authenticationHelper, callback);
};

function getDeviceToken() {
  if (typeof window === "undefined") return "";
  try {
    return localStorage.getItem(DEVICE_TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

function setDeviceToken(token) {
  if (!token || typeof window === "undefined") return;
  try {
    localStorage.setItem(DEVICE_TOKEN_KEY, token);
  } catch {
    // ignore
  }
}

function getCaptchaSatisfied() {
  return false;
}

function setCaptchaSatisfied(flag) {
  return flag;
}

function parseJwt(token) {
  try {
    const payload = token.split(".")[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded);
  } catch {
    return {};
  }
}

function renderChecklist(checks = []) {
  if (!Array.isArray(checks) || !checks.length) return null;
  return (
    <div className="section">
      <h3>Checks</h3>
      <div className="checklist">
        {checks.map((c, idx) => {
          const state = c.state || (c.pass ? "pass" : "fail");
          const stateLabel = state === "not_applicable" ? "N/A" : state.toUpperCase();
          return (
            <div key={idx} className={`check ${state}`}>
              <span><strong className="check-state">{stateLabel}</strong> {c.label || c.title || c.id}</span>
              {c.info && <small>{c.info}</small>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function renderIssues(issues = []) {
  if (!Array.isArray(issues) || !issues.length) return null;
  return (
    <div className="section">
      <h3>Top Findings</h3>
      <ul className="issues">
        {issues.map((i, idx) => {
          const severity = (i.severity || "").toLowerCase();
          const impact = typeof i.scoreImpact === "number" ? ` (${i.scoreImpact})` : "";
          return (
            <li key={idx}>
              <strong className={`sev ${severity}`}>{severity.toUpperCase()}</strong> {i.title || i.message}{impact}
              {i.reason && <small>{i.reason}</small>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function getResultWebsite(result, fallbackUrl = "") {
  return result?.requestedUrl || result?.target?.input || result?.url || fallbackUrl;
}

function getFetchedWebsite(result, fallbackUrl = "") {
  return result?.fetchedUrl || result?.target?.canonical || result?.url || fallbackUrl;
}

function getResultScore(result) {
  return result?.scoreBreakdown?.finalScore ?? result?.score ?? result?.healthScore ?? "-";
}

function App() {
  const [scanUrl, setScanUrl] = useState("https://example.com");
  const [scanStatus, setScanStatus] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [captchaReady, setCaptchaReady] = useState(false);
  const [captchaSatisfied, setCaptchaSatisfiedState] = useState(false);
  const [freeCreditsUsed, setFreeCreditsUsed] = useState(() => {
    try {
      return Number(localStorage.getItem(FREE_CREDITS_KEY) || "0");
    } catch {
      return 0;
    }
  });
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [authMode, setAuthMode] = useState("signin");
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState("");
  const [signupSuccess, setSignupSuccess] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [forgotStage, setForgotStage] = useState("request");
  const [forgotCode, setForgotCode] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");
  const [showLogin, setShowLogin] = useState(false);
  const [authTokens, setAuthTokens] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [needLogin, setNeedLogin] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  const captchaRef = useRef(null);
  const widgetIdRef = useRef(null);
  const demoParamLoadedRef = useRef(false);

  const authed = Boolean(authTokens?.idToken);

  useEffect(() => {
    trackLandingViewed();
  }, []);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    const existing = document.querySelector('script[src="https://challenges.cloudflare.com/turnstile/v0/api.js"]');
    if (existing) {
      setCaptchaReady(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = () => setCaptchaReady(true);
    script.onerror = () => setCaptchaError("Captcha failed to load. Try again.");
    document.body.appendChild(script);
    return () => {
      script.remove();
    };
  }, []);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || captchaSatisfied) return;
    if (!captchaReady || !captchaRef.current) return;
    if (!window.turnstile) return;
    widgetIdRef.current = window.turnstile.render(captchaRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (token) => {
        setCaptchaToken(token);
        setCaptchaError("");
        setCaptchaSatisfiedState(true);
        setCaptchaSatisfied(true);
      },
      "error-callback": () => setCaptchaError("Verification failed. Please retry."),
      "expired-callback": () => {
        setCaptchaToken("");
        setCaptchaSatisfiedState(false);
        setCaptchaSatisfied(false);
      },
    });
    return () => {
      if (window.turnstile && widgetIdRef.current !== null && widgetIdRef.current !== undefined) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [captchaReady, captchaSatisfied]);

  const pool = new CognitoUserPool({ UserPoolId: COGNITO_POOL_ID, ClientId: COGNITO_CLIENT_ID });

  const handleLogin = (e) => {
    e.preventDefault();
    if (!loginEmail.trim() || !loginPassword) {
      setLoginError("Email and password required.");
      return;
    }
    setLoginLoading(true);
    setLoginError("");
    const user = new CognitoUser({ Username: loginEmail.trim(), Pool: pool });
    user.setAuthenticationFlowType("USER_PASSWORD_AUTH");
    const authDetails = new AuthenticationDetails({ Username: loginEmail.trim(), Password: loginPassword });
    try {
      user.authenticateUser(authDetails, {
        onSuccess: (result) => {
          const idToken = result.getIdToken().getJwtToken();
          const accessToken = result.getAccessToken().getJwtToken();
          const refreshToken = result.getRefreshToken()?.getToken();
          const payload = parseJwt(idToken) || {};
          const sub = payload.sub;
          const email = payload.email || loginEmail.trim();
          setAuthTokens({ idToken, accessToken, refreshToken });
          setAuthUser({ email, sub });
          if (sub) identifyUser(sub);
          trackEvent(EventNames.LoginCompleted, { user_id: sub });
          setShowLogin(false);
          setNeedLogin(false);
          setLoginPassword("");
          setLoginLoading(false);
        },
        onFailure: (err) => {
          if (err?.code === "UserNotConfirmedException") {
            setAuthMode("confirm");
            setSignupSuccess("Please enter the verification code sent to your email.");
          } else {
            setLoginError(err?.message || "Login failed");
          }
          setLoginLoading(false);
        },
        newPasswordRequired: () => {
          setLoginError("Password reset required via hosted UI");
          setLoginLoading(false);
        },
        mfaRequired: () => {
          setLoginError("MFA required via hosted UI");
          setLoginLoading(false);
        },
        totpRequired: () => {
          setLoginError("TOTP required via hosted UI");
          setLoginLoading(false);
        },
      });
    } catch (err) {
      setLoginError(err?.message || "Login failed");
      setLoginLoading(false);
    }
  };

  const handleSignup = (e) => {
    e.preventDefault();
    if (!loginEmail.trim() || !loginPassword) {
      setSignupError("Email and password required.");
      return;
    }
    setSignupLoading(true);
    setSignupError("");
    setSignupSuccess("");
    trackEvent(EventNames.SignupStarted, { email_hint: loginEmail.trim() });
    pool.signUp(loginEmail.trim(), loginPassword, [], null, (err) => {
      if (err) {
        setSignupError(err.message || "Sign up failed.");
        setSignupLoading(false);
        return;
      }
      setSignupSuccess("Check your email for a verification code.");
      setAuthMode("confirm");
      trackEvent(EventNames.SignupCompleted, { email_hint: loginEmail.trim() });
      setSignupLoading(false);
    });
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    if (!loginEmail.trim() || !confirmCode.trim()) {
      setConfirmError("Email and code required.");
      return;
    }
    setConfirmLoading(true);
    setConfirmError("");
    const user = new CognitoUser({ Username: loginEmail.trim(), Pool: pool });
    user.confirmRegistration(confirmCode.trim(), true, (err) => {
      if (err) {
        setConfirmError(err.message || "Verification failed.");
        setConfirmLoading(false);
        return;
      }
      setSignupSuccess("Email verified. You can sign in now.");
      setAuthMode("signin");
      setConfirmLoading(false);
    });
  };

  const handleForgotRequest = (e) => {
    e.preventDefault();
    if (!loginEmail.trim()) {
      setForgotError("Email required.");
      return;
    }
    setForgotLoading(true);
    setForgotError("");
    setForgotSuccess("");
    const user = new CognitoUser({ Username: loginEmail.trim(), Pool: pool });
    user.forgotPassword({
      onSuccess: () => {
        setForgotSuccess("Code sent. Check your email.");
        setForgotStage("code");
        setForgotLoading(false);
      },
      onFailure: (err) => {
        setForgotError(err.message || "Could not send code.");
        setForgotLoading(false);
      },
      inputVerificationCode: () => {
        setForgotStage("code");
        setForgotLoading(false);
      },
    });
  };

  const handleForgotConfirm = (e) => {
    e.preventDefault();
    if (!loginEmail.trim() || !forgotCode.trim() || !forgotNewPassword) {
      setForgotError("Email, code, and new password required.");
      return;
    }
    setForgotLoading(true);
    setForgotError("");
    setForgotSuccess("");
    const user = new CognitoUser({ Username: loginEmail.trim(), Pool: pool });
    user.confirmPassword(forgotCode.trim(), forgotNewPassword, {
      onSuccess: () => {
        setForgotSuccess("Password reset. You can sign in now.");
        setAuthMode("signin");
        setForgotStage("request");
        setForgotLoading(false);
      },
      onFailure: (err) => {
        setForgotError(err.message || "Reset failed.");
        setForgotLoading(false);
      },
    });
  };

  const handleLogout = () => {
    setAuthTokens(null);
    setAuthUser(null);
  };

  const getAssessmentRequestHref = () => {
    const website = getResultWebsite(scanResult, scanUrl);
    return website ? `/request-assessment?website=${encodeURIComponent(website)}` : "/request-assessment";
  };

  const trackAssessmentCtaClick = (source) => {
    trackEvent(EventNames.AssessmentCtaClicked, {
      source,
      anon_id: getAnonId(),
      requested_url: getResultWebsite(scanResult, scanUrl),
      score: getResultScore(scanResult),
    });
  };

  const runScan = async (url) => {
    const anonId = getAnonId();
    const deviceToken = getDeviceToken();
    const captchaRequired = Boolean(TURNSTILE_SITE_KEY);
    const captchaToSend = captchaRequired ? captchaToken : "";

    if (!url || (!url.startsWith("http://") && !url.startsWith("https://"))) {
      setScanStatus("");
      setScanError("Enter a valid URL (include https://).");
      trackEvent("scan_failed", { reason: "invalid_url" });
      return;
    }
    if (!authed && freeCreditsUsed >= FREE_CREDIT_LIMIT) {
      setScanStatus("Free SEO Scan limit reached.");
      setScanError("Sign in to keep scanning.");
      setNeedLogin(true);
      trackEvent(EventNames.PaywallShown, { reason: "free_credit_client" });
      return;
    }
    if (captchaRequired && !captchaToSend) {
      setScanStatus("");
      setScanError("Complete the verification to start your scan.");
      trackEvent("scan_failed", { reason: "captcha_missing" });
      return;
    }

    trackEvent(EventNames.ScanStarted, { url, anon_id: anonId, authed });
    setScanStatus("Launching scan...");
    setScanError("");
    setScanResult(null);
    setScanLoading(true);
    setNeedLogin(false);

    try {
      const headers = { "Content-Type": "application/json" };
      if (authTokens?.idToken) headers.Authorization = `Bearer ${authTokens.idToken}`;

      const res = await fetch(`${API_BASE}/scan`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          url,
          anon_id: anonId,
          captcha: captchaToSend,
          device_token: deviceToken || undefined,
        }),
      });

      const newDeviceToken = res.headers.get("x-device-token");
      if (newDeviceToken) setDeviceToken(newDeviceToken);

      const data = await res.json().catch(() => ({}));

      if (res.status === 401 && (data.error === "RequireLogin" || data.message === "RequireLogin")) {
        setScanStatus("Free SEO Scan limit reached.");
        setScanError("Sign in to run another scan.");
        setNeedLogin(true);
        setScanLoading(false);
        trackEvent(EventNames.PaywallShown, { reason: "backend_limit", anon_id: anonId });
        return;
      }

      if (res.status === 429 || data.error === "limit_reached") {
        setScanStatus("Free SEO Scan limit reached.");
        setScanError("Sign in to continue scanning.");
        setNeedLogin(true);
        setScanLoading(false);
        trackEvent(EventNames.PaywallShown, { reason: "rate_limit", anon_id: anonId });
        return;
      }

      if (!res.ok) {
        throw new Error(data.error || `Scan failed (${res.status})`);
      }

      setScanResult(data);
      setScanStatus("Scan complete");
      setScanLoading(false);
      trackEvent(EventNames.ScanCompleted, { anon_id: anonId, requested_url: url, score: getResultScore(data) });
      trackEvent(EventNames.Activation, { anon_id: anonId, authed });

      if (!authed) {
        const nextUsed = freeCreditsUsed + 1;
        setFreeCreditsUsed(nextUsed);
        try {
          localStorage.setItem(FREE_CREDITS_KEY, String(nextUsed));
        } catch {
          // ignore
        }
      }
    } catch (err) {
      setScanError(err.message || "Scan failed.");
      setScanStatus("");
      setScanResult(null);
      setScanLoading(false);
      trackEvent("scan_failed", { message: err.message || "unknown", anon_id: anonId });
    }
  };

  const handleScrollToScan = (ctaName = "scan") => {
    trackEvent("ScanCtaClicked", { cta: ctaName, anon_id: getAnonId() });
    document.getElementById("audit-form")?.scrollIntoView({ behavior: "smooth" });
  };

  const loadDemoReport = async () => {
    setDemoLoading(true);
    setScanError("");
    try {
      const res = await fetch("/demo_report.json");
      const data = await res.json();
      const demoWebsite = data.target?.canonical || data.target?.input || "https://example.com";
      setScanResult({
        score: data.summary?.score ?? 72,
        requestedUrl: data.target?.input,
        fetchedUrl: data.target?.canonical,
        checks: data.checks,
        issues: data.issues,
        findings: data.checks,
        next_actions: data.next_actions,
      });
      setScanStatus("Sample report (demo)");
      setNeedLogin(false);
      trackEvent("DemoReportViewed", { anon_id: getAnonId() });
      document.getElementById("audit-form")?.scrollIntoView({ behavior: "smooth" });
    } catch {
      setScanError("Could not load demo report.");
    } finally {
      setDemoLoading(false);
    }
  };

  useEffect(() => {
    if (demoParamLoadedRef.current || typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("demo") === "1") {
      demoParamLoadedRef.current = true;
      loadDemoReport();
    }
  }, []);

  const isSignup = authMode === "signup";
  const isConfirm = authMode === "confirm";
  const isForgot = authMode === "forgot";

  return (
    <div className="app">
      <div className="top-nav">
        <div className="brand">OptiSiteSEO</div>
        <div className="nav-links">
          <a className="link" href="/">Home</a>
          <a className="link" href="/demo/index.html">Demo</a>
          <a className="link" href="/privacy/index.html">Privacy</a>
          <a
            className="primary"
            href="#scan"
            onClick={(e) => {
              e.preventDefault();
              handleScrollToScan("nav_cta");
            }}
          >
            Run Free SEO Scan
          </a>
          <AuthHeader authed={authed} email={authUser?.email} onLogin={() => { setAuthMode("signin"); setShowLogin(true); }} onLogout={handleLogout} />
        </div>
      </div>

      <div className="scan-hero">
        <div className="scan-hero-copy">
          <p className="eyebrow">Technical SEO + visibility infrastructure</p>
          <h1>Find the technical SEO and visibility issues limiting discoverability.</h1>
          <p className="subtext">
            The Free SEO Scan checks public technical SEO and visibility signals and flags issues that can affect discoverability, indexing,
            crawlability, performance, mobile usability, analytics, and measurement. Results are recommendations, not outcome guarantees.
          </p>
        </div>
        <div className="scan-hero-actions">
          <button className="primary" onClick={() => handleScrollToScan("hero_primary")}>Run Free SEO Scan</button>
          <button className="secondary" onClick={loadDemoReport} disabled={demoLoading}>
            {demoLoading ? "Loading demo..." : "View demo report"}
          </button>
          {!authed && <p className="hint">Free SEO Scans used: {freeCreditsUsed}/{FREE_CREDIT_LIMIT}. Sign in to keep scanning.</p>}
        </div>
      </div>

      <div className="card section" id="audit-form">
        <p className="eyebrow">Free SEO Scan</p>
        <h2>Check a URL for technical SEO issues</h2>
        <p className="hint">
          The Free SEO Scan checks indexability, crawlability, metadata, headings, structured data, performance risk, mobile basics, and page
          structure. Use it to spot discoverability blockers before deciding what to fix first.
        </p>
        <div className="form-grid">
          <input
            type="text"
            value={scanUrl}
            onChange={(e) => setScanUrl(e.target.value)}
            placeholder="https://www.yoursite.com"
          />
        
          <button className="primary" onClick={() => runScan(scanUrl)} disabled={scanLoading}>
            {scanLoading ? "Scanning..." : "Run Free SEO Scan"}
          </button>
        </div>
        {TURNSTILE_SITE_KEY ? (
          <div className="captcha-container">
            <div ref={captchaRef} />
            {captchaError && <div className="error">{captchaError}</div>}
          </div>
        ) : (
          <p className="hint">Set REACT_APP_TURNSTILE_SITE_KEY to enable bot protection on scans.</p>
        )}
        {needLogin && (
          <div className="notice">
            Free SEO Scan limit reached. <button onClick={() => { setAuthMode("signin"); setShowLogin(true); }}>Sign in</button> or <button onClick={() => { setAuthMode("signup"); setShowLogin(true); }}>create an account</button> to continue.
          </div>
        )}
        {scanStatus && <div className="status">{scanStatus}</div>}
        {scanError && <div className="error">{scanError}</div>}
      </div>

      {scanResult && (
        <div className="card section">
          <h2>Results</h2>
          <div className="summary">
            <div>
              <p className="label">Score</p>
              <div className="score-badge">{getResultScore(scanResult)}</div>
            </div>
            <div>
              <p className="label">Requested</p>
              <p>{getResultWebsite(scanResult, scanUrl)}</p>
            </div>
            <div>
              <p className="label">Fetched</p>
              <p>{getFetchedWebsite(scanResult, scanUrl)}</p>
            </div>
          </div>
          <p className="hint score-copy">Score is based on active technical, visibility, lead-path, and measurement findings.</p>
          {renderChecklist(scanResult.checks || scanResult.checklist)}
          {renderIssues(scanResult.issues || scanResult.findings)}
          {scanResult.next_actions && Array.isArray(scanResult.next_actions) && (
            <div className="section">
              <h3>Next actions</h3>
              <ul>
                {scanResult.next_actions.map((n, idx) => (
                  <li key={idx}>{n.label || n.title || n.action} {n.eta ? `(${n.eta})` : ""}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="paid-audit-cta">
            <div className="paid-audit-copy">
              <p className="eyebrow">Technical SEO & Visibility Assessment</p>
              <h3>Need help prioritizing and fixing these technical SEO issues?</h3>
              <p>
                A Technical SEO & Visibility Assessment gives you a manual review of your site's technical SEO, crawlability, indexing, performance,
                AI discoverability, and measurement gaps, plus a prioritized implementation roadmap.
              </p>
              <ul className="audit-scope-list">
                <li>Reviews the scan result, priority pages, metadata, structured data, rendering risks, and technical visibility issues.</li>
                <li>Prioritizes crawlability and indexing problems, performance and measurement gaps, and AI discoverability readiness.</li>
                <li>Creates a first-next-later roadmap before implementation is scoped.</li>
                <li>Does not include backlink campaigns, broad keyword strategy, content marketing retainers, paid ads management, or outcome guarantees.</li>
              </ul>
              <p className="fine-print">Recommendations are not outcome guarantees.</p>
            </div>
            <aside className="audit-offer-box" aria-label="Technical SEO & Visibility Assessment details">
              <div className="audit-fact">
                <span className="label">Assessment price</span>
                <strong>$297-$1,500</strong>
              </div>
              <div className="audit-fact">
                <span className="label">Next paid step</span>
                <strong>Manual diagnostic</strong>
              </div>
              <div className="audit-fact">
                <span className="label">After assessment</span>
                <strong>Implementation package</strong>
              </div>
              <a className="primary" href={getAssessmentRequestHref()} onClick={() => trackAssessmentCtaClick("scan_results_upsell")}>
                Request Assessment
              </a>
              <a className="secondary" href="/technical-visibility-audit" onClick={() => trackAssessmentCtaClick("scan_results_details")}>
                View Assessment Details
              </a>
            </aside>
          </div>
        </div>
      )}

      {showLogin && (
        <div className="modal">
          <div className="modal-card">
            <h3>
              {isSignup && "Create account"}
              {isConfirm && "Enter verification code"}
              {isForgot && (forgotStage === "request" ? "Reset password" : "Confirm reset")}
              {!isSignup && !isConfirm && !isForgot && "Sign in"}
            </h3>

            {isConfirm ? (
              <form onSubmit={handleConfirm} className="login-form">
                <label>Email</label>
                <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required />
                <label>Verification code</label>
                <input type="text" value={confirmCode} onChange={(e) => setConfirmCode(e.target.value)} required />
                {confirmError && <div className="error">{confirmError}</div>}
                {signupSuccess && <div className="status">{signupSuccess}</div>}
                <div className="login-actions">
                  <button type="submit" className="primary" disabled={confirmLoading}>{confirmLoading ? "Verifying..." : "Verify"}</button>
                  <button type="button" className="secondary" onClick={() => setShowLogin(false)}>Cancel</button>
                </div>
                <div className="switcher">
                  <button type="button" className="link-btn" onClick={() => setAuthMode("signin")}>Back to sign in</button>
                </div>
              </form>
            ) : isForgot ? (
              <form onSubmit={forgotStage === "request" ? handleForgotRequest : handleForgotConfirm} className="login-form">
                <label>Email</label>
                <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required />
                {forgotStage === "code" && (
                  <>
                    <label>Reset code</label>
                    <input type="text" value={forgotCode} onChange={(e) => setForgotCode(e.target.value)} required />
                    <label>New password</label>
                    <input type="password" value={forgotNewPassword} onChange={(e) => setForgotNewPassword(e.target.value)} required />
                  </>
                )}
                {forgotError && <div className="error">{forgotError}</div>}
                {forgotSuccess && <div className="status">{forgotSuccess}</div>}
                <div className="login-actions">
                  <button type="submit" className="primary" disabled={forgotLoading}>
                    {forgotLoading ? "Working..." : forgotStage === "request" ? "Send code" : "Update password"}
                  </button>
                  <button type="button" className="secondary" onClick={() => { setShowLogin(false); setForgotStage("request"); }}>Cancel</button>
                </div>
                <div className="switcher">
                  {forgotStage === "code" ? (
                    <button type="button" className="link-btn" onClick={() => setForgotStage("request")}>Need a new code? Resend</button>
                  ) : (
                    <button type="button" className="link-btn" onClick={() => setAuthMode("signin")}>Back to sign in</button>
                  )}
                </div>
              </form>
            ) : (
              <form onSubmit={isSignup ? handleSignup : handleLogin} className="login-form">
                <label>Email</label>
                <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required />
                <label>Password</label>
                <input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required />
                {loginError && !isSignup && <div className="error">{loginError}</div>}
                {signupError && isSignup && <div className="error">{signupError}</div>}
                {signupSuccess && isSignup && <div className="status">{signupSuccess}</div>}
                <div className="login-actions">
                  <button type="submit" className="primary" disabled={isSignup ? signupLoading : loginLoading}>
                    {isSignup ? (signupLoading ? "Signing up..." : "Sign up") : loginLoading ? "Signing in..." : "Sign in"}
                  </button>
                  <button type="button" className="secondary" onClick={() => setShowLogin(false)}>Cancel</button>
                </div>
                <div className="switcher">
                  {isSignup ? (
                    <button type="button" className="link-btn" onClick={() => { setAuthMode("signin"); setSignupError(""); setSignupSuccess(""); }}>
                      Already have an account? Sign in
                    </button>
                  ) : (
                    <>
                      <button type="button" className="link-btn" onClick={() => { setAuthMode("signup"); setLoginError(""); }}>
                        Need an account? Create one
                      </button>
                      <button type="button" className="link-btn" onClick={() => { setAuthMode("forgot"); setForgotStage("request"); setForgotError(""); setForgotSuccess(""); }}>
                        Forgot password?
                      </button>
                    </>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
